import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  CreditCard,
  X,
  CheckCircle,
  XCircle,
  Clock,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { usePayments } from '@/hooks/usePayments';
import { paymentsApi } from '@/lib/api/payments.api';

import type { PaymentLink } from '@/lib/api/payments.api';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'CREATED', label: 'Created' },
  { value: 'SENT', label: 'Sent' },
  { value: 'OPENED', label: 'Opened' },
  { value: 'PAID', label: 'Paid' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_BADGE_CLASSES: Record<PaymentLink['status'], string> = {
  CREATED: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  SENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  OPENED: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  PAID: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  EXPIRED: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  CANCELLED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const STATUS_ACTIONS: Array<{ status: string; label: string; icon: typeof CheckCircle }> = [
  { status: 'PAID', label: 'Mark as Paid', icon: CheckCircle },
  { status: 'FAILED', label: 'Mark as Failed', icon: XCircle },
  { status: 'EXPIRED', label: 'Mark as Expired', icon: Clock },
];

const reservationBadgeClass = (status: string | null): string => {
  if (!status) return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
  if (status === 'CREATED')
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (status === 'FAILED') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'; // SKIPPED_*
};

const reservationLabel = (status: string | null): string => {
  if (!status) return '—';
  return status
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const DEFAULT_LIMIT = 25;

function StatusActionMenu({
  payment,
  onUpdate,
}: {
  payment: PaymentLink;
  onUpdate: (id: string, status: string) => void;
}) {
  const [open, setOpen] = useState(false);

  if (payment.status === 'PAID') return null;

  return (
    <div className="relative">
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setOpen(!open)}>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
            {STATUS_ACTIONS.filter((a) => a.status !== payment.status).map((action) => (
              <button
                key={action.status}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-700"
                onClick={() => {
                  onUpdate(payment.id, action.status);
                  setOpen(false);
                }}
              >
                <action.icon className="h-3.5 w-3.5" />
                {action.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function PaymentsPage() {
  usePageTitle('Payments');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;

  const [reservationDetail, setReservationDetail] = useState<PaymentLink | null>(null);

  const { data, isLoading } = usePayments({
    status: status || undefined,
    page,
    limit,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, newStatus }: { id: string; newStatus: string }) =>
      paymentsApi.updateStatus(id, newStatus),
    onSuccess: (_, { newStatus }) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast({ title: `Payment marked as ${newStatus.toLowerCase()}` });
    },
    onError: () => {
      toast({ title: 'Failed to update payment status', variant: 'destructive' });
    },
  });

  const retryReservationMutation = useMutation({
    mutationFn: (id: string) => paymentsApi.retryReservation(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      const created = result.reservation_status === 'CREATED';
      toast({
        title: created
          ? 'Reservation created'
          : `Reservation ${reservationLabel(result.reservation_status)}`,
        description: created ? undefined : result.reservation_error || undefined,
        variant: created ? undefined : 'destructive',
      });
      // Reflect the new outcome in the open dialog (if it's still this row).
      setReservationDetail((prev) => (prev && prev.id === id ? { ...prev, ...result } : prev));
    },
    onError: () => {
      toast({ title: 'Failed to retry reservation', variant: 'destructive' });
    },
  });

  const payments = data?.data ?? [];
  const pagination = data?.pagination;

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = Boolean(status);

  const formatAmount = (amount: string, currency: string) => {
    const symbol = currency === 'GBP' ? '£' : currency;
    return `${symbol}${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Payments</p>
        <h1 className="text-3xl font-bold tracking-tight">Payment Links</h1>
        <p className="text-muted-foreground max-w-3xl">Payment links created by the AI agent</p>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Payment Links</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-[160px]">
              <label
                htmlFor="status-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Status
              </label>
              <select
                id="status-filter"
                value={status}
                onChange={(e) => updateParams({ status: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading payments...</p>
              </div>
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
              <CreditCard className="text-muted-foreground mb-3 h-10 w-10" />
              <p className="text-muted-foreground text-sm">
                {hasActiveFilters
                  ? 'No payment links match the current filters.'
                  : 'No payment links have been created yet.'}
              </p>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="mt-3" onClick={clearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="border-b px-3 py-2 font-medium">Date</th>
                    <th className="border-b px-3 py-2 font-medium">Vehicle</th>
                    <th className="border-b px-3 py-2 font-medium">Amount</th>
                    <th className="border-b px-3 py-2 font-medium">Customer</th>
                    <th className="border-b px-3 py-2 font-medium">Phone</th>
                    <th className="border-b px-3 py-2 font-medium">Status</th>
                    <th className="border-b px-3 py-2 font-medium">Reservation</th>
                    <th className="border-b px-3 py-2 font-medium">Call</th>
                    <th className="border-b px-3 py-2 font-medium">Payment URL</th>
                    <th className="border-b px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b last:border-b-0 dark:border-slate-700">
                      <td className="px-3 py-3 text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="px-3 py-3">
                        {payment.vehicle_name ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{payment.vehicle_name}</span>
                            {payment.vehicle_registration && (
                              <span className="text-muted-foreground font-mono text-[11px] tracking-wider uppercase">
                                {payment.vehicle_registration}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 font-medium whitespace-nowrap">
                        {formatAmount(payment.amount, payment.currency)}
                      </td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap text-gray-600 dark:text-gray-300">
                        {payment.customer_name ?? <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap text-gray-600 dark:text-gray-300">
                        {payment.phone_number ?? <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[payment.status]}`}
                        >
                          {payment.status.charAt(0) + payment.status.slice(1).toLowerCase()}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {payment.reservation_status ? (
                          <button
                            type="button"
                            onClick={() => setReservationDetail(payment)}
                            className="inline-flex items-center gap-1"
                            title="View reservation payload & response"
                          >
                            <span
                              className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${reservationBadgeClass(payment.reservation_status)}`}
                            >
                              {reservationLabel(payment.reservation_status)}
                            </span>
                          </button>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {payment.call_id ? (
                          <Link
                            to={`/calls/${payment.call_id}`}
                            className="text-primary text-xs hover:underline"
                          >
                            View call
                          </Link>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <a
                          href={payment.payment_link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                        >
                          Open
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                      <td className="px-3 py-3">
                        <StatusActionMenu
                          payment={payment}
                          onUpdate={(id, newStatus) =>
                            updateStatusMutation.mutate({ id, newStatus })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <div className="mt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
                resourceName="payments"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!reservationDetail}
        onOpenChange={(open) => !open && setReservationDetail(null)}
      >
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle className="text-base">
              DealerKit Reservation — {reservationDetail?.vehicle_registration || '—'}
            </DialogTitle>
          </DialogHeader>
          {reservationDetail && (
            <div className="max-h-[70vh] space-y-4 overflow-auto">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span>
                  <span className="text-muted-foreground">Status: </span>
                  <span className="font-medium">
                    {reservationLabel(reservationDetail.reservation_status)}
                  </span>
                </span>
                {reservationDetail.reservation_id && (
                  <span>
                    <span className="text-muted-foreground">Reservation ID: </span>
                    <span className="font-mono">{reservationDetail.reservation_id}</span>
                  </span>
                )}
              </div>

              {reservationDetail.reservation_error && (
                <div>
                  <p className="mb-1 text-xs font-semibold tracking-wide uppercase">Error</p>
                  <pre className="bg-destructive/10 text-destructive overflow-auto rounded-md p-3 font-mono text-xs whitespace-pre-wrap">
                    {reservationDetail.reservation_error}
                  </pre>
                </div>
              )}

              <div>
                <p className="mb-1 text-xs font-semibold tracking-wide uppercase">
                  Request payload
                </p>
                <pre className="bg-muted max-h-60 overflow-auto rounded-md p-3 font-mono text-xs">
                  {reservationDetail.reservation_payload
                    ? JSON.stringify(reservationDetail.reservation_payload, null, 2)
                    : '—'}
                </pre>
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold tracking-wide uppercase">Response</p>
                <pre className="bg-muted max-h-60 overflow-auto rounded-md p-3 font-mono text-xs">
                  {reservationDetail.reservation_response
                    ? JSON.stringify(reservationDetail.reservation_response, null, 2)
                    : '—'}
                </pre>
              </div>

              {reservationDetail.reservation_status !== 'CREATED' && (
                <div className="flex items-center justify-end gap-3 border-t pt-3">
                  <p className="text-muted-foreground mr-auto text-xs">
                    Retries the reservation and emails the result.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => retryReservationMutation.mutate(reservationDetail.id)}
                    disabled={retryReservationMutation.isPending}
                  >
                    <RefreshCw
                      className={`mr-1.5 h-3.5 w-3.5 ${retryReservationMutation.isPending ? 'animate-spin' : ''}`}
                    />
                    {retryReservationMutation.isPending ? 'Retrying…' : 'Retry reservation'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
